import { VpxAction } from './actions';
import * as ActionType from './constants';

const INIT_STATE = {
    list: null,
    listLoading: false,
    listError: null,
    listTotalCount: null,
    listTruncated: false,
    userProfile: null,
    // The user whose profile was last requested; with profileLoading false, its
    // fetch has finished (userProfile or profileError holds the result).
    profileUserId: null,
    organizationProfile: null,
    profileLoading: false,
    profileError: false,
    // The failed response of the last Person Profile fetch (`{ status, body }`), if any.
    profileFailure: null,
    actionError: null,
    actionSuccessMessage: null,
    externalList: null,
    externalListLoading: false,
    externalListError: null,
    profilePreview: null,
    profileIsPreview: false,
};

type State = {
    list?: {};
    listLoading: boolean;
    listError?: string;
    userProfile?: {};
    profileUserId?: number;
    organizationProfile?: {};
    profileLoading: boolean;
    profileError: boolean;
    profileFailure?: { status: number, body?: any };
    actionError?: string;
    actionSuccessMessage?: string;
    externalList: {};
    externalListLoading: boolean;
    externalListError?: string;
    profilePreview: ?Object;
    profileIsPreview: boolean;
};

const mapChangeValuePropositionActions = (vpElement, targetVp, newAction) => {
    if (vpElement?.Details?.VpID === targetVp && !!vpElement?.Actions) {
        vpElement.Actions = { ...vpElement.Actions, ...newAction } ;
    }
    return vpElement;
}

const Vpx = (state: State = INIT_STATE, action: VpxAction) => {
    switch (action.type) {
        case ActionType.SEARCH_VPX_VP_EXCHANGE_LIST:
            return { ...state, listLoading: true, listError: null, listTotalCount: null, listTruncated: false };
        case ActionType.SEARCH_VPX_VP_EXCHANGE_LIST_SUCCESS:
            return {
                ...state,
                list: action.payload.vpxList,
                listTotalCount: action.payload.totalCount,
                listTruncated: !!action.payload.truncated,
                listLoading: false,
            };
        case ActionType.SEARCH_VPX_VP_EXCHANGE_LIST_FAILED:
            return { ...state, list: [], listError: action.payload, listLoading: false, listTotalCount: null, listTruncated: false };

        case ActionType.VALUE_PROPOSITION_VIEW_USER_PROFILE:
            return { ...state, profileLoading: true, profileError: null, profileFailure: null, userProfile: null, profileUserId: action.payload.userId };
        case ActionType.VALUE_PROPOSITION_VIEW_USER_PROFILE_SUCCESS:
            return { ...state, profileLoading: false, userProfile: action.payload.userPofile };
        case ActionType.VALUE_PROPOSITION_VIEW_USER_PROFILE_FAILED:
            return { ...state, profileLoading: false, profileError: action.payload, profileFailure: action.failure };

        case ActionType.VALUE_PROPOSITION_VIEW_ORGANIZATION_PROFILE:
            return { ...state, profileLoading: true, profileError: null, profileFailure: null, userProfile: null, profileUserId: null };
        case ActionType.VALUE_PROPOSITION_VIEW_ORGANIZATION_PROFILE_SUCCESS:
            return { ...state, profileLoading: false, organizationProfile: action.payload };
        case ActionType.VALUE_PROPOSITION_VIEW_ORGANIZATION_PROFILE_FAILED:
            return { ...state, profileLoading: false, profileError: action.payload };

        case ActionType.VALUE_PROPOSITION_VIEW_USER_PROFILE_BY_SHARE_KEY:
            return { ...state, profileLoading: true, profileError: null, profileFailure: null, userProfile: null, profileUserId: null, profilePreview: null, profileIsPreview: false };
        case ActionType.VALUE_PROPOSITION_VIEW_USER_PROFILE_BY_SHARE_KEY_FULL_SUCCESS:
            return { ...state, profileLoading: false, userProfile: action.payload.userPofile, profileIsPreview: false };
        case ActionType.VALUE_PROPOSITION_VIEW_USER_PROFILE_BY_SHARE_KEY_PREVIEW_SUCCESS:
            return { ...state, profileLoading: false, profilePreview: action.payload.preview, profileIsPreview: true };
        case ActionType.VALUE_PROPOSITION_VIEW_USER_PROFILE_BY_SHARE_KEY_FAILED:
            return { ...state, profileLoading: false, profileError: action.payload };

        case ActionType.SYNC_USER_PROFILE_OWNER_AVATAR: {
            const avatarUrl = action.payload?.avatarUrl;
            if (!avatarUrl || !state.userProfile) {
                return state;
            }
            const ownerKey = state.userProfile.Owner != null ? 'Owner' : 'owner';
            const prevOwner = state.userProfile[ownerKey];
            if (!prevOwner) {
                return state;
            }
            const syncedOwner = {
                ...prevOwner,
                ownerAvatarURL: avatarUrl,
                OwnerAvatarURL: avatarUrl,
            };
            const syncedVpList = state.userProfile.ValuePropositions?.map((row) => {
                const rowOwnerKey = row?.Owner != null ? 'Owner' : 'owner';
                const rowOwner = row[rowOwnerKey];
                if (!rowOwner) {
                    return row;
                }
                return {
                    ...row,
                    [rowOwnerKey]: {
                        ...rowOwner,
                        ownerAvatarURL: avatarUrl,
                        OwnerAvatarURL: avatarUrl,
                    },
                };
            });
            return {
                ...state,
                userProfile: {
                    ...state.userProfile,
                    [ownerKey]: syncedOwner,
                    ...(syncedVpList ? { ValuePropositions: syncedVpList } : {}),
                },
            };
        }

        case ActionType.LOAD_EXTERNAL_LIST:
            return { ...state, externalList: null, externalListLoading: true, externalListError: null };
        case ActionType.LOAD_EXTERNAL_LIST_SUCCESS:
            return { ...state, externalList: action.payload.extenalList, externalListLoading: false };
        case ActionType.LOAD_EXTERNAL_LIST_FAILED:
            return { ...state, externalListLoading: false, externalListError: action.payload };

        case ActionType.CLEAR_VALUE_PROPOSITION_PROFILE_ERROR:
            return { ...state, profileError: null, profileFailure: null };
        case ActionType.ACCEPT_TO_FOLLOW:
            return { ...state, accepting: true, error: null };
        case ActionType.ACCEPT_TO_FOLLOW_SUCCESS:
            return { ...state, accepting: false, error: null, actionSuccessMessage: 'Accepted to follow the value proposition successfully.' };
        case ActionType.ACCEPT_TO_FOLLOW_FAILED:
            return { ...state, accepting: false, error: action.payload };
        case ActionType.REQUEST_TO_FOLLOW:
            return { ...state, listLoading: true, actionError: null, actionSuccessMessage: null };
        case ActionType.REQUEST_TO_FOLLOW_SUCCESS: {
            let newState = { ...state, listLoading: false, actionSuccessMessage: 'Request to follow the value proposition was sent successfully.' };
            if (!state.list) {
                return newState;
            }
            const newVpList = state.list.map(vpElement => mapChangeValuePropositionActions(
                vpElement, action.payload.vpId, { CanFollow: false, WaitingForApproval: true }
            ));
            newState = { ...newState, list: newVpList }

            const newUserProfileVpList = state.userProfile?.ValuePropositions?.map(vpElement =>
                mapChangeValuePropositionActions(
                    vpElement, action.payload.vpId, { CanFollow: false, WaitingForApproval: true }
                )
            );
            if (!!newUserProfileVpList) {
                newState = {
                    ...newState,
                    userProfile: { ...state.userProfile, ValuePropositions: newUserProfileVpList }
                };
            }

            const newOrganizationProfileVpList = state.organizationProfile?.ValuePropositions?.map(vpElement =>
                mapChangeValuePropositionActions(
                    vpElement, action.payload.vpId, { CanFollow: false, WaitingForApproval: true }
                )
            );
            if (!!newOrganizationProfileVpList) {
                newState = {
                    ...newState,
                    organizationProfile: { ...state.organizationProfile, ValuePropositions: newOrganizationProfileVpList }
                };
            }
            return newState;
        }
        case ActionType.REQUEST_TO_FOLLOW_FAILED:
            return { ...state, actionError: action.payload, listLoading: false };

        case ActionType.REQUEST_TO_UNFOLLOW:
            return { ...state, listLoading: true, actionError: null };
        case ActionType.REQUEST_TO_UNFOLLOW_SUCCESS: {
            const newVpList = state.list.map(vpElement => mapChangeValuePropositionActions(
                vpElement, action.payload.vpId, { CanFollow: true, CanUnfollow: false, WaitingForApproval: false }
            ));
            let newState = { ...state, list: newVpList, listLoading: false };

            const newUserProfileVpList = state.userProfile?.ValuePropositions?.map(vpElement =>
                mapChangeValuePropositionActions(
                    vpElement, action.payload.vpId, { CanFollow: true, CanUnfollow: false, WaitingForApproval: false }
                )
            );
            if (!!newUserProfileVpList) {
                newState = {
                    ...newState,
                    userProfile: { ...state.userProfile, ValuePropositions: newUserProfileVpList }
                };
            }

            const newOrganizationProfileVpList = state.organizationProfile?.ValuePropositions?.map(vpElement =>
                mapChangeValuePropositionActions(
                    vpElement, action.payload.vpId, { CanFollow: true, CanUnfollow: false, WaitingForApproval: false }
                )
            );
            if (!!newOrganizationProfileVpList) {
                newState = {
                    ...newState,
                    organizationProfile: { ...state.organizationProfile, ValuePropositions: newOrganizationProfileVpList }
                };
            }
            return newState;
        }
        case ActionType.REQUEST_TO_UNFOLLOW_FAILED:
            return { ...state, actionError: action.payload, listLoading: false };
        default:
            return { ...state };
    }
}

export default Vpx;
